// Reading a site's market descriptor (djin-cloud#1089, ADR-089 A1 and M3).
//
// The descriptor is the one "this site has a market" signal and the one source of the
// market's labels, roles and timezone. Components ask for a ROLE ("the settlement price")
// and print the product's own label; none names an ISO product itself.

import type { SiteConfig } from '../../types/project.types';
import type { MarketDescriptor, MarketDescriptorProduct, MarketRole } from '../../types/market.types';

/** The Market tab's gate: the site has a stored descriptor. No site loaded = no market. */
export function hasMarket(site: SiteConfig | null | undefined): site is SiteConfig & { market_descriptor: MarketDescriptor } {
  return site?.market_descriptor != null;
}

/** The first product filling `role`, or null when the descriptor leaves the role unfilled. */
export function productForRole(descriptor: MarketDescriptor, role: MarketRole): MarketDescriptorProduct | null {
  return descriptor.products.find((p) => p.roles.includes(role)) ?? null;
}

/** The zone's short name at `at` (CDT or CST for America/Chicago), from tzdata, never hardcoded. */
export function timezoneAbbreviation(timezone: string, at: Date): string {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: timezone, timeZoneName: 'short' })
      .formatToParts(at)
      .find((p) => p.type === 'timeZoneName');
    return part?.value ?? timezone;
  } catch {
    return timezone;
  }
}

/**
 * `-$5.23`, never `$-5.23`. Real-time prices go negative in oversupply (routine at
 * LZ_WEST), so the sign is ordinary, not an edge case (djin-cloud#907 review).
 */
export function formatUsdPerMwh(v: number): string {
  const magnitude = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${v < 0 ? '-' : ''}$${magnitude}`;
}

/** What the tab calls each role. The product's own label says which product fills it. */
export const ROLE_TITLES: Record<MarketRole, string> = {
  dispatch_signal: 'Dispatch signal',
  settlement_price: 'Settlement price',
  forward_price: 'Forward price',
};

/**
 * Words for a settlement point type. The descriptor carries the code only (ERCOT: LZ, HB,
 * RN); a code this table lacks is shown as it is, never guessed.
 */
const POINT_TYPE_LABELS: Record<string, string> = { LZ: 'Load zone', HB: 'Hub', RN: 'Resource node' };

export function pointTypeLabel(pointType: string): string {
  return POINT_TYPE_LABELS[pointType] ?? pointType;
}
